import { useEffect, useRef, useState } from "react";
import {
  readAdminView,
  useAdminViewRestoring,
  writeAdminView,
} from "../../lib/admin/adminViewState";
import { Link } from "react-router-dom";
import { apiFetch } from "../../api/client";
import { formatDate, formatNumber, formatRelative } from "../../lib/admin/adminFormat";

type Member = { user_id: string; email: string | null; role: string; created_at: string };
type ShareLink = {
  id: string;
  role: string;
  expires_at: string | null;
  max_uses: number | null;
  uses_count: number;
  revoked_at: string | null;
  created_at: string;
  created_by_email: string | null;
};
type Checkpoint = {
  id: string;
  title: string;
  source_revision: string;
  created_at: string;
  created_by_email: string | null;
};
type Visit = {
  id: string;
  visited_at: string;
  left_at: string | null;
  duration_sec: number | null;
  access_kind: string;
  visitor_label: string | null;
  visitor_email: string | null;
  ip: string | null;
};
type BoardEvent = {
  id: string;
  revision: string;
  created_at: string;
  actor_email: string | null;
  kind: string | null;
};

export type AdminBoardDetails = {
  board: {
    id: string;
    title: string;
    created_at: string;
    updated_at: string;
    in_trash: boolean;
    guest_policy: string;
    public_link_mode: string;
    owner_user_id: string | null;
    owner_email: string | null;
    is_guest: boolean;
    creator_ip: string | null;
    folder_title: string | null;
    objects: number;
    revision: number;
  };
  members: Member[];
  share_links: ShareLink[];
  checkpoints: Checkpoint[];
  visits: Visit[];
  events: BoardEvent[];
  online: { peers: number; silent: number } | null;
};

const ACCESS_LABELS: Record<string, string> = {
  session: "Аккаунт",
  share_link: "Ссылка",
  guest_session: "Гость (ссылка)",
  guest_edit: "Гостевая доска",
  admin_spectate: "Админ",
};

function shareLinkState(l: ShareLink): string {
  if (l.revoked_at) return "отозвана";
  if (l.expires_at && new Date(l.expires_at).getTime() < Date.now()) return "истекла";
  if (l.max_uses !== null && l.uses_count >= l.max_uses) return "исчерпана";
  return "активна";
}

function cacheKeyFor(boardId: string): string {
  return `admin:boardCard:${boardId}`;
}

export function AdminBoardCard({ boardId }: { boardId: string }) {
  const restoring = useAdminViewRestoring();
  /** Возврат «Назад»: карточка сразу на месте, иначе прокрутка не дотянется до неё. */
  const restoredRef = useRef(
    restoring ? readAdminView<AdminBoardDetails>(cacheKeyFor(boardId)) ?? null : null
  );
  const [data, setData] = useState<AdminBoardDetails | null>(restoredRef.current);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(!restoredRef.current);

  useEffect(() => {
    const restored = restoredRef.current;
    if (restored && restored.board.id === boardId) return;
    // Открыли другую доску — запомненная карточка больше не нужна.
    restoredRef.current = null;
    let cancelled = false;
    setLoading(true);
    setErr("");
    setData(null);
    apiFetch<AdminBoardDetails>(`/api/v1/admin/boards/${encodeURIComponent(boardId)}/details`)
      .then((d) => {
        if (cancelled) return;
        writeAdminView(cacheKeyFor(boardId), d);
        setData(d);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Не удалось загрузить доску");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [boardId]);

  if (loading) return <p className="wb-cabinet-plan-hint">Загрузка доски…</p>;
  if (err) {
    return (
      <p className="wb-cabinet-toast wb-cabinet-toast--err" role="alert">
        {err}
      </p>
    );
  }
  if (!data) return null;
  const b = data.board;

  return (
    <div className="wb-admin-dossier">
      <h2 className="wb-admin-users__title">
        {b.title || "Без названия"}{" "}
        {b.in_trash ? <span className="wb-admin-users__count">в корзине</span> : null}
        {data.online && data.online.peers > 0 ? (
          <span className="wb-admin-users__count">сейчас на доске: {data.online.peers}</span>
        ) : null}
      </h2>
      <p className="wb-cabinet-plan-hint__text">
        {b.is_guest ? (
          <>Гостевая доска{b.creator_ip ? ` · IP создателя ${b.creator_ip}` : ""}</>
        ) : (
          <>Владелец: {b.owner_email ?? "—"}</>
        )}
        {b.folder_title ? ` · Папка: ${b.folder_title}` : ""} · Объектов:{" "}
        {formatNumber(b.objects)} · Ревизия: {formatNumber(b.revision)}
      </p>
      <p className="wb-cabinet-plan-hint__text" style={{ marginTop: 6 }}>
        Создана {formatDate(b.created_at)} · Изменена {formatDate(b.updated_at)} · Гости:{" "}
        {b.guest_policy} · Публичная ссылка: {b.public_link_mode}
      </p>
      <p className="wb-cabinet-plan-hint__actions" style={{ marginTop: 8 }}>
        {!b.in_trash ? (
          <Link
            className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
            to={`/board/${b.id}?spectate=1`}
            title="Невидимый вход: только смотреть, участники не видят"
          >
            Открыть невидимо
          </Link>
        ) : null}
        <span className="wb-admin-table__mono">{b.id}</span>
      </p>

      <h3 className="wb-admin-dossier__section">Участники ({data.members.length})</h3>
      <div className="wb-admin-table-wrap">
        <table className="wb-admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Роль</th>
              <th>Добавлен</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.user_id}>
                <td>{m.email ?? m.user_id.slice(0, 8)}</td>
                <td>{m.role === "editor" ? "Редактор" : "Читатель"}</td>
                <td title={formatDate(m.created_at)}>{formatRelative(m.created_at)}</td>
              </tr>
            ))}
            {!data.members.length ? (
              <tr>
                <td colSpan={3}>Только владелец</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <h3 className="wb-admin-dossier__section">
        Ссылки доступа ({data.share_links.length})
      </h3>
      <div className="wb-admin-table-wrap">
        <table className="wb-admin-table">
          <thead>
            <tr>
              <th>Состояние</th>
              <th>Роль</th>
              <th>Переходов</th>
              <th>Истекает</th>
              <th>Создал</th>
            </tr>
          </thead>
          <tbody>
            {data.share_links.map((l) => (
              <tr key={l.id}>
                <td>{shareLinkState(l)}</td>
                <td>{l.role}</td>
                <td>
                  {l.uses_count}
                  {l.max_uses !== null ? ` из ${l.max_uses}` : ""}
                </td>
                <td>{l.expires_at ? formatDate(l.expires_at) : "бессрочно"}</td>
                <td>{l.created_by_email ?? "—"}</td>
              </tr>
            ))}
            {!data.share_links.length ? (
              <tr>
                <td colSpan={5}>Ссылок нет</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <h3 className="wb-admin-dossier__section">Снимки ({data.checkpoints.length})</h3>
      <div className="wb-admin-table-wrap">
        <table className="wb-admin-table">
          <thead>
            <tr>
              <th>Название</th>
              <th>Ревизия</th>
              <th>Создан</th>
              <th>Кем</th>
            </tr>
          </thead>
          <tbody>
            {data.checkpoints.map((c) => (
              <tr key={c.id}>
                <td>{c.title}</td>
                <td>{c.source_revision}</td>
                <td title={formatDate(c.created_at)}>{formatRelative(c.created_at)}</td>
                <td>{c.created_by_email ?? "—"}</td>
              </tr>
            ))}
            {!data.checkpoints.length ? (
              <tr>
                <td colSpan={4}>Снимков нет</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <h3 className="wb-admin-dossier__section">Последние визиты ({data.visits.length})</h3>
      <div className="wb-admin-table-wrap">
        <table className="wb-admin-table">
          <thead>
            <tr>
              <th>Кто</th>
              <th>Как зашёл</th>
              <th>IP</th>
              <th>Когда</th>
            </tr>
          </thead>
          <tbody>
            {data.visits.map((v) => (
              <tr key={v.id}>
                <td>{v.visitor_email ?? v.visitor_label ?? "Гость"}</td>
                <td>{ACCESS_LABELS[v.access_kind] ?? v.access_kind}</td>
                <td className="wb-admin-table__mono">{v.ip ?? "—"}</td>
                <td title={formatDate(v.visited_at)}>{formatRelative(v.visited_at)}</td>
              </tr>
            ))}
            {!data.visits.length ? (
              <tr>
                <td colSpan={4}>Визитов нет</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <h3 className="wb-admin-dossier__section">История правок ({data.events.length})</h3>
      <div className="wb-admin-table-wrap">
        <table className="wb-admin-table">
          <thead>
            <tr>
              <th>Ревизия</th>
              <th>Что</th>
              <th>Кто</th>
              <th>Когда</th>
            </tr>
          </thead>
          <tbody>
            {data.events.map((e) => (
              <tr key={e.id}>
                <td>{e.revision}</td>
                <td>{e.kind ?? "правка"}</td>
                <td>{e.actor_email ?? "гость"}</td>
                <td title={formatDate(e.created_at)}>{formatRelative(e.created_at)}</td>
              </tr>
            ))}
            {!data.events.length ? (
              <tr>
                <td colSpan={4}>Записей нет</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
